import { IconDotsVerticalFilled } from '@tabler/icons-react';
import { Button, Tooltip } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Dropdown } from '../../dropdown/Dropdown.tsx';
import { TbIcon } from '../../icon/TbIcon.tsx';
import { useConfigurationCardEntries } from '../ConfigurationCardContext.tsx';
import { sortEntriesByDomOrder } from '../sortEntriesByDomOrder.ts';
import { CONFIGURATION_CARD_MENU_SLOT_CLASS } from './ConfigurationCardMenuSlot.tsx';
import { scrollToConfigurationCard } from './scrollToConfigurationCard.ts';

// Etichetta del bottone menu (tooltip + aria-label). Stringa fissa: la UI di
// configurazione in Admin non è tradotta.
const SECTIONS_MENU_LABEL = 'Menu delle sezioni';

/**
 * Menu di navigazione tra le card del group (vista `dense`): bottone con
 * l'elenco delle card principali, click = scroll alla card. Va montato DENTRO
 * il group (dove vive il context delle card registrate) ma si mostra
 * nell'header del Drawer: proietta il bottone via Portal nello slot
 * `ConfigurationCardMenuSlot` passato come `extra` del Drawer. Lo slot viene
 * cercato risalendo al `.ant-drawer-content` che contiene il group — niente
 * id globali, funziona anche coi Drawer annidati. Senza slot (o fuori da un
 * Drawer) non renderizza nulla.
 *
 * Con una sola card il bottone è disabilitato: non c'è dove navigare.
 */
export function ConfigurationCardSectionsMenu() {
  const entries = useConfigurationCardEntries();
  // Solo le card principali (niente sub-card), in ordine DOM reale.
  const topLevel = useMemo(
    () => sortEntriesByDomOrder(entries.filter(entry => !entry.parentId)),
    [entries],
  );

  // Marker invisibile nel body: punto di partenza per risalire al Drawer che
  // contiene QUESTO group e trovarne lo slot nell'header.
  const markerRef = useRef<HTMLSpanElement>(null);
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const drawer = markerRef.current?.closest('.ant-drawer-content');
    setSlot(
      drawer?.querySelector<HTMLElement>(
        `.${CONFIGURATION_CARD_MENU_SLOT_CLASS}`,
      ) ?? null,
    );
  }, []);

  const menuItems = useMemo(
    () =>
      topLevel.map(entry => ({
        key: entry.id,
        icon: entry.icon,
        label: entry.label ?? entry.title,
      })),
    [topLevel],
  );

  const menu = (
    <Dropdown
      placement="bottomLeft"
      menu={{
        items: menuItems,
        onClick: ({ key }) => scrollToConfigurationCard(key),
      }}
    >
      <Tooltip title={SECTIONS_MENU_LABEL} placement="left">
        <Button
          type="text"
          icon={<TbIcon icon={IconDotsVerticalFilled} />}
          aria-label={SECTIONS_MENU_LABEL}
          disabled={topLevel.length <= 1}
        />
      </Tooltip>
    </Dropdown>
  );

  return (
    <>
      <span ref={markerRef} hidden />
      {slot != null && createPortal(menu, slot)}
    </>
  );
}
